/**
 * Validators - Shared validation utilities for data integrity
 * Used across all services to ensure consistent validation
 */

/**
 * Custom ValidationError class
 */
export class ValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Validate required field
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @throws {ValidationError} If value is null, undefined, or empty string
 */
export const validateRequired = (value, fieldName) => {
  if (value === null || value === undefined || value === '') {
    throw new ValidationError(`${fieldName} là bắt buộc`);
  }
};

/**
 * Validate amount (must be positive number)
 * @param {number} amount - Amount to validate
 * @param {string} fieldName - Field name for error message (default: 'Số tiền')
 * @throws {ValidationError} If amount is not a positive number
 */
export const validateAmount = (amount, fieldName = 'Số tiền') => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    throw new ValidationError(`${fieldName} phải là số hợp lệ`);
  }
  if (amount <= 0) {
    throw new ValidationError(`${fieldName} phải lớn hơn 0`);
  }
};

/**
 * Validate year
 * @param {number} year - Year to validate
 * @throws {ValidationError} If year is invalid
 */
export const validateYear = (year) => {
  if (typeof year !== 'number' || isNaN(year)) {
    throw new ValidationError('Năm phải là số hợp lệ');
  }
  if (year < 2020 || year > 2100) {
    throw new ValidationError('Năm phải trong khoảng 2020-2100');
  }
};

/**
 * Validate month
 * @param {number} month - Month to validate (1-12)
 * @throws {ValidationError} If month is invalid
 */
export const validateMonth = (month) => {
  if (typeof month !== 'number' || isNaN(month)) {
    throw new ValidationError('Tháng phải là số hợp lệ');
  }
  if (month < 1 || month > 12) {
    throw new ValidationError('Tháng phải trong khoảng 1-12');
  }
};

/**
 * Validate user ID
 * @param {string} userId - User ID to validate
 * @throws {ValidationError} If userId is invalid
 */
export const validateUserId = (userId) => {
  validateRequired(userId, 'User ID');
  if (typeof userId !== 'string') {
    throw new ValidationError('User ID phải là chuỗi');
  }
};

/**
 * Validate document ID
 * @param {string} docId - Document ID to validate
 * @param {string} docType - Type of document (for error message)
 * @throws {ValidationError} If docId is invalid
 */
export const validateDocId = (docId, docType = 'Document') => {
  validateRequired(docId, `${docType} ID`);
  if (typeof docId !== 'string') {
    throw new ValidationError(`${docType} ID phải là chuỗi`);
  }
};

/**
 * Validate date (must be valid Date object or Timestamp)
 * @param {Date|Timestamp|string} date - Date to validate
 * @param {string} fieldName - Field name for error message
 * @throws {ValidationError} If date is invalid
 */
export const validateDate = (date, fieldName = 'Ngày') => {
  if (!date) {
    throw new ValidationError(`${fieldName} là bắt buộc`);
  }
  
  // Check if it's a Firestore Timestamp or Date
  const isTimestamp = date.toDate && typeof date.toDate === 'function';
  const isDate = date instanceof Date;
  const isString = typeof date === 'string';
  
  if (!isTimestamp && !isDate && !isString) {
    throw new ValidationError(`${fieldName} phải là ngày hợp lệ`);
  }
  
  // Validate Date is not Invalid Date
  if (isDate && isNaN(date.getTime())) {
    throw new ValidationError(`${fieldName} không hợp lệ`);
  }
  
  // Validate string date format
  if (isString) {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      throw new ValidationError(`${fieldName} không hợp lệ`);
    }
  }
};

/**
 * Validate string length
 * @param {string} value - String to validate
 * @param {number} maxLength - Maximum length
 * @param {string} fieldName - Field name for error message
 * @throws {ValidationError} If string exceeds max length
 */
export const validateStringLength = (value, maxLength, fieldName) => {
  if (typeof value !== 'string') {
    throw new ValidationError(`${fieldName} phải là chuỗi`);
  }
  if (value.length > maxLength) {
    throw new ValidationError(`${fieldName} không được vượt quá ${maxLength} ký tự`);
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @throws {ValidationError} If email format is invalid
 */
export const validateEmail = (email) => {
  validateRequired(email, 'Email');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new ValidationError('Email không hợp lệ');
  }
};

/**
 * Validate phone number (Vietnamese format)
 * @param {string} phone - Phone number to validate
 * @throws {ValidationError} If phone format is invalid
 */
export const validatePhone = (phone) => {
  if (!phone) return; // Optional field
  
  const phoneRegex = /^(0|\+84)[0-9]{9,10}$/;
  if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
    throw new ValidationError('Số điện thoại không hợp lệ');
  }
};

/**
 * Validate object has required keys
 * @param {object} obj - Object to validate
 * @param {string[]} requiredKeys - Array of required key names
 * @param {string} objectName - Object name for error message
 * @throws {ValidationError} If object is missing required keys
 */
export const validateObjectKeys = (obj, requiredKeys, objectName = 'Dữ liệu') => {
  if (!obj || typeof obj !== 'object') {
    throw new ValidationError(`${objectName} phải là object hợp lệ`);
  }
  
  const missingKeys = requiredKeys.filter(key => !(key in obj));
  if (missingKeys.length > 0) {
    throw new ValidationError(
      `${objectName} thiếu trường: ${missingKeys.join(', ')}`
    );
  }
};

/**
 * Validate array is not empty
 * @param {array} arr - Array to validate
 * @param {string} fieldName - Field name for error message
 * @throws {ValidationError} If array is empty or not an array
 */
export const validateNonEmptyArray = (arr, fieldName) => {
  if (!Array.isArray(arr)) {
    throw new ValidationError(`${fieldName} phải là mảng`);
  }
  if (arr.length === 0) {
    throw new ValidationError(`${fieldName} không được rỗng`);
  }
};

/**
 * Validate enum value
 * @param {any} value - Value to validate
 * @param {array} allowedValues - Array of allowed values
 * @param {string} fieldName - Field name for error message
 * @throws {ValidationError} If value is not in allowed values
 */
export const validateEnum = (value, allowedValues, fieldName) => {
  if (!allowedValues.includes(value)) {
    throw new ValidationError(
      `${fieldName} phải là một trong: ${allowedValues.join(', ')}`
    );
  }
};

/**
 * Safe validation wrapper - returns {success, error} instead of throwing
 * @param {function} validationFn - Validation function to execute
 * @returns {object} {success: boolean, error: string|null}
 */
export const safeValidate = (validationFn) => {
  try {
    validationFn();
    return { success: true, error: null };
  } catch (error) {
    if (error instanceof ValidationError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: 'Lỗi validation không xác định' };
  }
};

/**
 * Validate planned income/expense data
 * @param {object} data - Data to validate
 * @param {string} type - 'income' or 'expense'
 * @throws {ValidationError} If data is invalid
 */
export const validatePlannedData = (data, type = 'income') => {
  const typeName = type === 'income' ? 'Dự thu' : 'Dự chi';
  
  validateObjectKeys(
    data,
    ['amount', 'month', 'year', 'categoryName'],
    typeName
  );
  
  validateAmount(data.amount, 'Số tiền');
  validateMonth(data.month);
  validateYear(data.year);
  validateRequired(data.categoryName, 'Danh mục');
  
  // Optional: validate plannedDate if provided
  if (data.plannedDate) {
    validateDate(data.plannedDate, 'Ngày dự kiến');
  }
  
  // Optional: validate recurring settings
  if (data.isRecurring) {
    if (typeof data.isRecurring !== 'boolean') {
      throw new ValidationError('isRecurring phải là boolean');
    }
    if (data.isRecurring && !data.recurringDay) {
      throw new ValidationError('Khoản định kỳ phải có ngày lặp lại');
    }
    if (data.recurringDay && (data.recurringDay < 1 || data.recurringDay > 31)) {
      throw new ValidationError('Ngày lặp lại phải trong khoảng 1-31');
    }
  }
};

/**
 * Validate actual income/expense data
 * @param {object} data - Data to validate
 * @param {string} type - 'income' or 'expense'
 * @throws {ValidationError} If data is invalid
 */
export const validateActualData = (data, type = 'income') => {
  const typeName = type === 'income' ? 'Thu nhập' : 'Chi tiêu';
  
  validateObjectKeys(
    data,
    ['amount', 'transactionDate'],
    typeName
  );
  
  validateAmount(data.amount, 'Số tiền');
  validateDate(data.transactionDate, 'Ngày giao dịch');
  
  // Optional: validate walletId if provided
  if (data.walletId) {
    validateDocId(data.walletId, 'Ví');
  }
};

export default {
  ValidationError,
  validateRequired,
  validateAmount,
  validateYear,
  validateMonth,
  validateUserId,
  validateDocId,
  validateDate,
  validateStringLength,
  validateEmail,
  validatePhone,
  validateObjectKeys,
  validateNonEmptyArray,
  validateEnum,
  safeValidate,
  validatePlannedData,
  validateActualData
};
